import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../admin/Modal';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { getScoreColor } from '../../../config/scoring';
import { useUnitPreferences, formatWeight, formatWater } from '../../utils/unitConversions';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * CLIENT DAY SUMMARY MODAL - Phase 2 Client Context
 *
 * Detailed view of a single day's health data.
 * Compact layout with Score + Weight header, health metrics, and nutrition details.
 *
 * Layout:
 * - Header: Score + Weight side by side (big impact values)
 * - Two columns: Health Metrics | Nutrition
 * - Nutrition includes: Donut chart + detailed breakdown table
 */

interface GoalTarget {
  value: number;
  goal_value?: number;
  type?: 'over' | 'under' | 'target' | 'boolean';
  goal_type?: 'over' | 'under' | 'target' | 'boolean';
  variance?: number;
  variance_percent?: number;
  visibility?: 'visible' | 'hidden';
  status?: 'enabled' | 'disabled';
}

interface DailyHealthData {
  date: string;
  actual_values?: {
    weight?: number;
    steps?: number;
    calories?: number;
    sleep_hours?: number;
    water_intake?: number;
    energy_level?: number;
    stress_level?: number;
  };
  weight?: number;
  steps?: number;
  calories?: number;
  sleep?: number;
  water?: number;
  workouts?: number;
  energy_level?: number;
  stress_level?: number;
  notes?: string;
  score?: number;
  status?: string;
  targets_at_time?: {
    steps?: GoalTarget;
    water_intake?: GoalTarget;
    sleep_hours?: GoalTarget;
    calories?: GoalTarget;
  };
  tierStatus?: 'tier3' | 'tier2' | 'tier1' | 'none';
  softScore?: number | null;
}

interface NutritionData {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  fiber_g?: number;
  sugar_g?: number;
  sodium_mg?: number;
  saturated_fat_g?: number;
  polyunsaturated_fat_g?: number;
  monounsaturated_fat_g?: number;
  cholesterol_mg?: number;
  potassium_mg?: number;
}

interface ClientDaySummaryModalProps {
  data: DailyHealthData;
  onClose: () => void;
  previousWeight?: number; // For showing weight delta
}

// getScoreColor imported from config/scoring.js

const getProgressColor = (value: number, target: number, type: string = 'over', variance: number = 10): string => {
  if (type === 'target') {
    const varianceAmount = target * (variance / 100);
    const lowerBound = target - varianceAmount;
    const upperBound = target + varianceAmount;
    if (value >= lowerBound && value <= upperBound) return '#10b981';
    if (value >= lowerBound * 0.9 && value <= upperBound * 1.1) return '#84cc16';
    if (value >= lowerBound * 0.8 && value <= upperBound * 1.2) return '#f59e0b';
    return '#ef4444';
  } else if (type === 'under') {
    if (value <= target) return '#10b981';
    if (value <= target * 1.1) return '#84cc16';
    if (value <= target * 1.25) return '#f59e0b';
    return '#ef4444';
  } else {
    if (value >= target) return '#10b981';
    if (value >= target * 0.85) return '#84cc16';
    if (value >= target * 0.7) return '#f59e0b';
    return '#ef4444';
  }
};

export const ClientDaySummaryModal: React.FC<ClientDaySummaryModalProps> = ({
  data,
  onClose,
  previousWeight
}) => {
  const prefs = useUnitPreferences();
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);
  const [loadingNutrition, setLoadingNutrition] = useState(false);

  useEffect(() => {
    const fetchNutrition = async () => {
      setLoadingNutrition(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/nutrition/daily/${data.date}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setNutritionData(result.data);
          }
        }
      } catch (err) {
        console.error('Error fetching nutrition data:', err);
      } finally {
        setLoadingNutrition(false);
      }
    };
    fetchNutrition();
  }, [data.date]);

  const getMetricValue = (field: string): number | undefined => {
    if (data.actual_values && data.actual_values[field as keyof typeof data.actual_values] !== undefined) {
      return data.actual_values[field as keyof typeof data.actual_values];
    }
    const legacyFieldMap: Record<string, string> = {
      'sleep_hours': 'sleep',
      'water_intake': 'water',
      'weight': 'weight'
    };
    const legacyField = legacyFieldMap[field] || field;
    return data[legacyField as keyof DailyHealthData] as number | undefined;
  };

  const getMetricTierColor = (targetField: string): string => {
    if (!data.targets_at_time) return '#6b7280';
    const target = data.targets_at_time[targetField as keyof typeof data.targets_at_time];
    if (!target) return '#6b7280';
    if ((target as any).visibility === 'hidden') return '#6b7280';
    if ((target as any).visibility === 'visible' && (target as any).status === 'enabled') {
      return '#10b981';
    } else if ((target as any).visibility === 'visible' && (target as any).status === 'disabled') {
      return '#06b6d4';
    }
    return '#6b7280';
  };

  const isMetricScored = (targetField: string): boolean => {
    if (!data.targets_at_time) return false;
    const target = data.targets_at_time[targetField as keyof typeof data.targets_at_time];
    if (!target) return false;
    return (target as any).visibility === 'visible' && (target as any).status === 'enabled';
  };

  const formatDate = (dateString: string): string => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const donutData = useMemo(() => {
    if (!nutritionData) return null;
    const protein = nutritionData.protein_g || 0;
    const carbs = nutritionData.carbs_g || 0;
    const fat = nutritionData.fats_g || 0;
    if (protein === 0 && carbs === 0 && fat === 0) return null;
    return {
      labels: ['Protein', 'Carbs', 'Fat'],
      datasets: [{
        data: [protein, carbs, fat],
        backgroundColor: ['#ef4444', '#f59e0b', '#3b82f6'],
        borderWidth: 0,
        hoverOffset: 4,
      }]
    };
  }, [nutritionData]);

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  };

  const realScore = data.score;
  const softScore = data.softScore;
  const tierStatus = data.tierStatus;
  const weight = getMetricValue('weight');

  const metrics = [
    { field: 'calories', targetField: 'calories', icon: '🔥', label: 'Calories', unit: '' },
    { field: 'steps', targetField: 'steps', icon: '👟', label: 'Steps', unit: '' },
    { field: 'water_intake', targetField: 'water_intake', icon: '💧', label: 'Water', unit: 'oz', isWater: true },
    { field: 'sleep_hours', targetField: 'sleep_hours', icon: '😴', label: 'Sleep', unit: 'hrs' },
  ];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={formatDate(data.date)}
      size="medium"
      variant="info"
      footer={
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="day-summary-content">
        {/* Compact Header: Score + Weight */}
        <div className="header-stats">
          {/* Score Card - Compact format: "88 (87)" */}
          {tierStatus === 'tier3' && realScore !== null && realScore !== undefined && (
            <div className="stat-card score-card">
              <div className="stat-value-row">
                <span className="stat-value" style={{ color: getScoreColor(Math.round(realScore)) }}>
                  {Math.round(realScore)}
                </span>
                {softScore !== null && softScore !== undefined && Math.round(softScore) !== Math.round(realScore) && (
                  <span className="stat-soft" style={{ color: getScoreColor(Math.round(softScore)) }}>
                    ({Math.round(softScore)})
                  </span>
                )}
              </div>
              <div className="stat-label">Score</div>
            </div>
          )}
          {tierStatus === 'tier2' && softScore !== null && softScore !== undefined && (
            <div className="stat-card score-card practice">
              <div className="stat-value" style={{ color: getScoreColor(Math.round(softScore)) }}>
                ({Math.round(softScore)})
              </div>
              <div className="stat-label">Practice</div>
            </div>
          )}

          {/* Weight Card - Value without unit, unit in label */}
          {weight && (
            <div className="stat-card weight-card">
              <div className="stat-value">{formatWeight(weight, prefs.units.weight, { showUnit: false })}</div>
              <div className="stat-label-row">
                <span>Weight ({prefs.units.weight})</span>
                {previousWeight && previousWeight !== weight && (
                  <span className="stat-delta-inline" style={{ color: weight < previousWeight ? '#10b981' : weight > previousWeight ? '#ef4444' : 'var(--text-tertiary)' }}>
                    {weight < previousWeight ? '↓' : '↑'}{Math.abs(weight - previousWeight).toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Calories Card */}
          {nutritionData && (
            <div className="stat-card calories-card">
              <div className="stat-value cal-green">{nutritionData.calories.toLocaleString()}</div>
              <div className="stat-label">Calories</div>
            </div>
          )}

          {/* Macros Donut + Legend */}
          {donutData && nutritionData && (
            <div className="stat-card macros-card">
              <div className="header-donut-container">
                <Doughnut data={donutData} options={donutOptions} />
              </div>
              <div className="header-macro-legend">
                <div className="header-legend-item">
                  <span className="legend-box protein">P</span>
                  <span className="header-legend-value">{(nutritionData.protein_g || 0).toFixed(1)}g</span>
                </div>
                <div className="header-legend-item">
                  <span className="legend-box carbs">C</span>
                  <span className="header-legend-value">{(nutritionData.carbs_g || 0).toFixed(1)}g</span>
                </div>
                <div className="header-legend-item">
                  <span className="legend-box fat">F</span>
                  <span className="header-legend-value">{(nutritionData.fats_g || 0).toFixed(1)}g</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Two Column Layout: Metrics + Nutrition */}
        <div className="summary-columns">
          {/* Left: Health Metrics */}
          <div className="metrics-column">
            <h4 className="section-title">Health Metrics</h4>

            {metrics.map(metric => {
              const value = getMetricValue(metric.field);
              if (value === undefined) return null;

              const target = data.targets_at_time?.[metric.targetField as keyof typeof data.targets_at_time];
              const targetValue = (target as any)?.goal_value || (target as any)?.value;
              const targetType = (target as any)?.goal_type || (target as any)?.type || 'over';
              const variance = (target as any)?.variance_percent || (target as any)?.variance || 10;
              const tierColor = getMetricTierColor(metric.targetField);
              const isScored = isMetricScored(metric.targetField);

              return (
                <div key={metric.field} className="metric-row">
                  <div className="metric-header">
                    <span className="metric-icon">{metric.icon}</span>
                    <span className="metric-label" style={{ color: tierColor }}>
                      {metric.label}
                      {isScored && <span className="scored-badge">Scored</span>}
                    </span>
                  </div>
                  <div className="metric-value-row">
                    <span className="metric-value">
                      {metric.isWater ? formatWater(value, prefs.units.water) : value.toLocaleString()}
                      {metric.unit && !metric.isWater && ` ${metric.unit}`}
                    </span>
                    {targetValue && (
                      <span className="metric-target">
                        / {metric.isWater ? formatWater(targetValue, prefs.units.water) : targetValue.toLocaleString()}
                        {metric.unit && !metric.isWater && ` ${metric.unit}`}
                      </span>
                    )}
                  </div>
                  {targetValue && (
                    <div className="metric-progress">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(100, (value / targetValue) * 100)}%`,
                          backgroundColor: getProgressColor(value, targetValue, targetType, variance)
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}

          </div>

          {/* Right: Nutrition */}
          <div className="nutrition-column">
            <h4 className="section-title">Nutrition</h4>

            {loadingNutrition && (
              <div className="loading-state">Loading...</div>
            )}

            {!loadingNutrition && !nutritionData && (
              <div className="no-data-state">No nutrition data</div>
            )}

            {!loadingNutrition && nutritionData && (
              <>
                {/* Detailed Nutrition Table */}
                <div className="nutrition-details">
                  <div className="detail-row">
                    <span className="detail-label">Sat Fat</span>
                    <span className="detail-value">{(nutritionData.saturated_fat_g || 0).toFixed(1)}g</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Fiber</span>
                    <span className="detail-value">{(nutritionData.fiber_g || 0).toFixed(1)}g</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Sugar</span>
                    <span className="detail-value">{(nutritionData.sugar_g || 0).toFixed(1)}g</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Cholesterol</span>
                    <span className="detail-value">{Math.round(nutritionData.cholesterol_mg || 0)}mg</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Sodium</span>
                    <span className="detail-value">{Math.round(nutritionData.sodium_mg || 0)}mg</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Potassium</span>
                    <span className="detail-value">{Math.round(nutritionData.potassium_mg || 0)}mg</span>
                  </div>
                </div>
              </>
            )}

            {/* Energy & Stress Tiles */}
            {(getMetricValue('energy_level') !== undefined || getMetricValue('stress_level') !== undefined) && (
              <div className="wellness-tiles">
                {getMetricValue('energy_level') !== undefined && (
                  <div className="wellness-tile">
                    <span className="wellness-value">{getMetricValue('energy_level')}</span>
                    <span className="wellness-label">Energy</span>
                  </div>
                )}
                {getMetricValue('stress_level') !== undefined && (
                  <div className="wellness-tile">
                    <span className="wellness-value">{getMetricValue('stress_level')}</span>
                    <span className="wellness-label">Stress</span>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Notes */}
        {data.notes && (
          <div className="notes-section">
            <h4 className="section-title">Notes</h4>
            <div className="notes-content">{data.notes}</div>
          </div>
        )}
      </div>

      <style>{`
        .day-summary-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* Header Stats: Score + Weight side by side */
        .header-stats {
          display: flex;
          justify-content: center;
          align-items: stretch;
          gap: 16px;
          padding: 12px 0;
        }

        .stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          padding: 12px 20px;
          background: var(--bg-tertiary);
          border-radius: var(--radius-medium);
          min-width: 100px;
        }

        .stat-value {
          font-size: 36px;
          font-weight: 700;
          line-height: 1;
          color: var(--text-primary);
          white-space: nowrap;
        }

        .stat-label {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 6px;
          white-space: nowrap;
        }

        /* Score card: inline "88 (87)" format */
        .stat-value-row {
          display: flex;
          align-items: baseline;
          gap: 4px;
          line-height: 1;
        }

        .stat-soft {
          font-size: 36px;
          font-weight: 700;
        }

        /* Weight label row with delta to the right */
        .stat-label-row {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 6px;
          gap: 6px;
          white-space: nowrap;
        }

        .stat-delta-inline {
          font-weight: 700;
        }

        .weight-card {
          min-width: 100px;
        }

        .score-card.practice .stat-value {
          font-size: 28px;
        }

        /* Calories card in header */
        .cal-green {
          color: var(--accent-green) !important;
        }

        /* Macros card in header */
        .macros-card {
          flex-direction: row !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 12px;
          padding: 12px 16px !important;
        }

        .header-donut-container {
          width: 64px;
          height: 64px;
        }

        .header-macro-legend {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .header-legend-value {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          min-width: 50px;
        }

        /* Two Column Layout */
        .summary-columns {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .section-title {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 12px 0;
          padding-bottom: 6px;
          border-bottom: 1px solid var(--border-secondary);
        }

        /* Metrics Column */
        .metrics-column {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .metric-row {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .metric-row.simple {
          flex-direction: row;
          align-items: center;
          gap: 8px;
        }

        .metric-header {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .metric-icon {
          font-size: 13px;
        }

        .metric-label {
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .scored-badge {
          font-size: 8px;
          padding: 1px 5px;
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          border-radius: 6px;
          text-transform: uppercase;
          font-weight: 600;
        }

        .metric-value-row {
          display: flex;
          align-items: baseline;
          gap: 4px;
          margin-left: 19px;
        }

        .metric-value {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .metric-target {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .metric-progress {
          height: 3px;
          background: var(--bg-tertiary);
          border-radius: 2px;
          overflow: hidden;
          margin-left: 19px;
        }

        .progress-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.3s ease;
        }

        .metric-row.simple .metric-label {
          flex: 1;
          color: var(--text-secondary);
        }

        .metric-row.simple .metric-value {
          font-size: 13px;
        }

        /* Nutrition Column */
        .nutrition-column {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        /* Legend boxes (used in header macros card) */
        .legend-box {
          width: 18px;
          height: 18px;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          color: white;
        }

        .legend-box.protein { background: #ef4444; }
        .legend-box.carbs { background: #f59e0b; }
        .legend-box.fat { background: #3b82f6; }

        /* Wellness Tiles (Energy/Stress) */
        .wellness-tiles {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }

        .wellness-tile {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 10px;
          background: var(--bg-tertiary);
          border-radius: var(--radius-small);
        }

        .wellness-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1;
        }

        .wellness-label {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 6px;
        }

        /* Nutrition Details Table - Vertical layout */
        .nutrition-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 10px;
          background: var(--bg-tertiary);
          border-radius: var(--radius-small);
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .detail-label {
          font-size: 11px;
          color: var(--text-tertiary);
        }

        .detail-value {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .loading-state,
        .no-data-state {
          text-align: center;
          padding: 20px;
          color: var(--text-tertiary);
          font-size: 12px;
        }

        /* Notes Section */
        .notes-section {
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }

        .notes-content {
          padding: 10px;
          background: var(--bg-tertiary);
          border-radius: var(--radius-medium);
          font-size: 13px;
          color: var(--text-primary);
          line-height: 1.4;
          font-style: italic;
        }

        /* Responsive */
        @media (max-width: 500px) {
          .summary-columns {
            grid-template-columns: 1fr;
          }
          .header-stats {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </Modal>
  );
};

export default ClientDaySummaryModal;
